import React, { useState } from 'react';
import { Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { MODE_OPTIONS, DIRECTION_OPTIONS, MOCKUP_STYLE_OPTIONS } from '../constants';
import type { CreateBrandTeamSessionRequest, MockupPreferences, MockupStyle } from '../../../../services/brandTeamService';
import Button from '../../../../components/Button';

interface StepSetupProps {
  formState: CreateBrandTeamSessionRequest;
  onFormChange: (updates: Partial<CreateBrandTeamSessionRequest>) => void;
  onLaunch: () => void;
  launching: boolean;
  availableAssets: Array<{ id: string; name: string }>;
}

const ASPECT_OPTIONS = [
  { value: 'auto' as const, label: 'Auto' },
  { value: 'square' as const, label: '1:1' },
  { value: 'landscape' as const, label: '16:9' },
  { value: 'portrait' as const, label: '9:16' },
];

const StepSetup: React.FC<StepSetupProps> = ({ formState, onFormChange, onLaunch, launching, availableAssets }) => {
  const [showPrefs, setShowPrefs] = useState(false);
  const prefs = formState.mockupPreferences || {};

  const updatePrefs = (updates: Partial<MockupPreferences>) => {
    onFormChange({ mockupPreferences: { ...prefs, ...updates } });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-text-dark">Configure Your Brand Session</h2>
        <p className="mt-1 text-sm text-body-grey">Choose a pack type, creative direction, and provide context for your AI team.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Pack Type */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-body-grey">Pack Type</label>
            <div className="grid grid-cols-2 gap-2">
              {MODE_OPTIONS.map((opt) => {
                const isSelected = formState.mode === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onFormChange({ mode: opt.value })}
                    className={`flex items-center gap-2.5 rounded-xl border-2 p-3 text-left transition-all ${
                      isSelected
                        ? 'border-brand-purple bg-primary-50 shadow-sm'
                        : 'border-primary-100 bg-white hover:border-primary-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                      isSelected ? 'bg-brand-purple text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-text-dark truncate">{opt.label}</div>
                      <div className="text-[10px] text-body-grey truncate">{opt.description}</div>
                    </div>
                    {isSelected && <Check className="ml-auto h-4 w-4 flex-shrink-0 text-brand-purple" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Direction */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-body-grey">Creative Direction</label>
            <div className="grid grid-cols-3 gap-3">
              {DIRECTION_OPTIONS.map((opt) => {
                const isSelected = formState.direction === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onFormChange({ direction: opt.value })}
                    className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all ${
                      isSelected
                        ? 'border-brand-purple bg-primary-50 shadow-sm'
                        : 'border-primary-100 bg-white hover:border-primary-200'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-brand-purple' : 'text-gray-400'}`} />
                    <div className="text-xs font-semibold text-text-dark">{opt.label}</div>
                    <div className="text-[10px] text-body-grey text-center">{opt.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mockup Style */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-body-grey">Mockup Style</label>
            <div className="grid grid-cols-2 gap-2">
              {MOCKUP_STYLE_OPTIONS.map((opt) => {
                const isSelected = (prefs.mockupStyle || 'auto') === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updatePrefs({ mockupStyle: opt.value as MockupStyle })}
                    className={`flex items-center gap-2.5 rounded-xl border-2 p-3 text-left transition-all ${
                      isSelected
                        ? 'border-brand-purple bg-primary-50 shadow-sm'
                        : 'border-primary-100 bg-white hover:border-primary-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                      isSelected ? 'bg-brand-purple text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-text-dark truncate">{opt.label}</div>
                      <div className="text-[10px] text-body-grey truncate">{opt.description}</div>
                    </div>
                    {isSelected && <Check className="ml-auto h-4 w-4 flex-shrink-0 text-brand-purple" />}
                  </button>
                );
              })}
            </div>
            {/* Scene Description (shown only for contextual-scene) */}
            {prefs.mockupStyle === 'contextual-scene' && (
              <div className="mt-3">
                <textarea
                  value={prefs.sceneDescription || ''}
                  onChange={(e) => updatePrefs({ sceneDescription: e.target.value.slice(0, 300) })}
                  placeholder="Describe the scene — e.g. 'A cozy cafe table with morning light', 'Minimalist workspace with a plant'..."
                  rows={2}
                  maxLength={300}
                  className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-xs text-text-dark placeholder-gray-400 outline-none transition-colors focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20"
                />
                <div className="text-right text-[10px] text-gray-400">{(prefs.sceneDescription || '').length}/300</div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-5">
          {/* Use Saved Brand Settings */}
          <label className="flex items-center gap-3 rounded-xl border border-primary-100 bg-white p-3.5 cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={formState.useSavedBrandSettings || false}
              onChange={(e) => onFormChange({ useSavedBrandSettings: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
            />
            <div>
              <div className="text-xs font-semibold text-text-dark">Use Saved Brand Settings</div>
              <div className="text-[10px] text-body-grey">Apply your saved brand profile to this session</div>
            </div>
          </label>

          {/* Business Context */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-body-grey">Business Context</label>
            <textarea
              value={formState.businessDescription || ''}
              onChange={(e) => onFormChange({ businessDescription: e.target.value })}
              placeholder="Describe your business, brand, and what you're promoting..."
              rows={3}
              className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-xs text-text-dark placeholder-gray-400 outline-none transition-colors focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20"
            />
          </div>

          {/* Objectives */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-body-grey">Objectives</label>
            <textarea
              value={formState.objectives || ''}
              onChange={(e) => onFormChange({ objectives: e.target.value })}
              placeholder="What are your goals for this session?"
              rows={3}
              className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-xs text-text-dark placeholder-gray-400 outline-none transition-colors focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20"
            />
          </div>

          {/* Assets */}
          {availableAssets.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-body-grey">
                Assets ({(formState.assetIds || []).length} selected)
              </label>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-primary-100 bg-white p-2 space-y-1">
                {availableAssets.slice(0, 10).map((asset) => {
                  const selected = (formState.assetIds || []).includes(asset.id);
                  return (
                    <label key={asset.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          const current = formState.assetIds || [];
                          onFormChange({
                            assetIds: selected ? current.filter((id) => id !== asset.id) : [...current, asset.id],
                          });
                        }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-brand-purple focus:ring-brand-purple"
                      />
                      <span className="text-xs text-text-dark truncate">{asset.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mockup Preferences (collapsible) */}
          <div className="rounded-xl border border-primary-100 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setShowPrefs(!showPrefs)}
              className="flex w-full items-center justify-between px-3.5 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="text-xs font-semibold text-text-dark">Mockup Preferences</span>
              {showPrefs ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>
            {showPrefs && (
              <div className="space-y-3 border-t border-primary-100 px-3.5 py-3">
                {/* Aspect Ratio */}
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-body-grey">Aspect Ratio</label>
                  <div className="flex gap-2">
                    {ASPECT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updatePrefs({ aspectRatio: opt.value })}
                        className={`rounded-lg px-3 py-1.5 text-[11px] font-medium transition-colors ${
                          (prefs.aspectRatio || 'auto') === opt.value
                            ? 'bg-brand-purple text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Toggles */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={prefs.includeBrandName ?? true} onChange={(e) => updatePrefs({ includeBrandName: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-brand-purple focus:ring-brand-purple" />
                  <span className="text-xs text-text-dark">Include Brand Name</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={prefs.includeTagline ?? false} onChange={(e) => updatePrefs({ includeTagline: e.target.checked })} className="h-3.5 w-3.5 rounded border-gray-300 text-brand-purple focus:ring-brand-purple" />
                  <span className="text-xs text-text-dark">Include Tagline</span>
                </label>
                {/* Style Notes */}
                <div>
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-body-grey">Style Notes</label>
                  <textarea
                    value={prefs.styleNotes || ''}
                    onChange={(e) => updatePrefs({ styleNotes: e.target.value.slice(0, 300) })}
                    placeholder="e.g. 'Merch worn by real humans', 'Outdoor nature scenes', 'Minimalist aesthetic'..."
                    rows={2}
                    maxLength={300}
                    className="w-full rounded-lg border border-primary-100 bg-gray-50 px-3 py-2 text-xs outline-none focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20"
                  />
                  <div className="text-right text-[10px] text-gray-400">{(prefs.styleNotes || '').length}/300</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Launch Button */}
      <div className="mt-8 flex justify-end">
        <Button variant="primary" size="large" onClick={onLaunch} disabled={launching || !formState.mode}>
          {launching ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Launching...</>
          ) : (
            'Launch Brand Team \u2192'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepSetup;
